import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IExperiment } from '../../types/experiment';

interface ExperimentExecutionControlsProps {
    experiment: IExperiment;
    onStart: () => Promise<void>;
    onPause: () => Promise<void>;
    onResume: () => Promise<void>;
    onStop: () => Promise<void>;
    disabled?: boolean;
}

const ExperimentExecutionControls: React.FC<ExperimentExecutionControlsProps> = ({
    experiment,
    onStart,
    onPause,
    onResume,
    onStop,
    disabled = false
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState<string | null>(null);
    const [confirmStop, setConfirmStop] = useState(false);

    const status = experiment.progress?.run_status || experiment.experiment_data?.status || 'draft';

    const handleAction = async (action: string, handler: () => Promise<void>) => {
        if (loading || disabled) return;
        setLoading(action);
        try {
            await handler();
        } finally {
            setLoading(null);
            setConfirmStop(false);
        }
    };

    const canStart = ['draft', 'cancelled', 'failed', 'completed'].includes(status);
    const canPause = status === 'running';
    const canResume = status === 'paused';
    const canStop = ['running', 'paused', 'pending'].includes(status);

    const handleStopClick = () => {
        if (confirmStop) {
            handleAction('stop', onStop);
        } else {
            setConfirmStop(true);
        }
    };

    return (
        <div className="experiment-execution-controls">
            <div className="execution-status">
                <span className={`status-indicator ${status}`} />
                <span className="status-text">
                    {t(`experiments.status.${status}`) || status}
                </span>
            </div>

            <div className="execution-buttons">
                {canStart && (
                    <button
                        className="execution-btn start"
                        onClick={() => handleAction('start', onStart)}
                        disabled={loading !== null || disabled}
                        title={t('experiments.execution.start')}
                    >
                        {loading === 'start' ? (
                            <span className="btn-spinner" />
                        ) : (
                            <span className="material-icons">play_arrow</span>
                        )}
                        {t('experiments.execution.start')}
                    </button>
                )}

                {canPause && (
                    <button
                        className="execution-btn pause"
                        onClick={() => handleAction('pause', onPause)}
                        disabled={loading !== null || disabled}
                        title={t('experiments.execution.pause')}
                    >
                        {loading === 'pause' ? (
                            <span className="btn-spinner" />
                        ) : (
                            <span className="material-icons">pause</span>
                        )}
                        {t('experiments.execution.pause')}
                    </button>
                )}

                {canResume && (
                    <button
                        className="execution-btn resume"
                        onClick={() => handleAction('resume', onResume)}
                        disabled={loading !== null || disabled}
                        title={t('experiments.execution.resume')}
                    >
                        {loading === 'resume' ? (
                            <span className="btn-spinner" />
                        ) : (
                            <span className="material-icons">play_arrow</span>
                        )}
                        {t('experiments.execution.resume')}
                    </button>
                )}

                {canStop && (
                    <button
                        className={`execution-btn stop ${confirmStop ? 'confirm' : ''}`}
                        onClick={handleStopClick}
                        onBlur={() => setConfirmStop(false)}
                        disabled={loading !== null || disabled}
                        title={confirmStop ? t('experiments.execution.confirmStop') : t('experiments.execution.stop')}
                    >
                        {loading === 'stop' ? (
                            <span className="btn-spinner" />
                        ) : (
                            <span className="material-icons">{confirmStop ? 'warning' : 'stop'}</span>
                        )}
                        {confirmStop ? t('experiments.execution.confirmStop') : t('experiments.execution.stop')}
                    </button>
                )}
            </div>

            {/* Execution hints */}
            <div className="execution-hints">
                {status === 'draft' && (
                    <p className="hint">
                        <span className="material-icons">info</span>
                        {t('experiments.execution.hints.draft')}
                    </p>
                )}
                {status === 'running' && (
                    <p className="hint">
                        <span className="material-icons">sync</span>
                        {t('experiments.execution.hints.running')}
                    </p>
                )}
                {status === 'paused' && (
                    <p className="hint">
                        <span className="material-icons">pause_circle</span>
                        {t('experiments.execution.hints.paused')}
                    </p>
                )}
                {status === 'failed' && (
                    <p className="hint error">
                        <span className="material-icons">error</span>
                        {t('experiments.execution.hints.failed')}
                    </p>
                )}
                {status === 'completed' && (
                    <p className="hint success">
                        <span className="material-icons">check_circle</span>
                        {t('experiments.execution.hints.completed')}
                    </p>
                )}
            </div>
        </div>
    );
};

export default ExperimentExecutionControls;
